import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, Warehouse, Droplets, Umbrella, Factory } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { scrollToSection } from "../lib/scroll";

const WORLDS = [
    {
        id: "christ",
        num: "01",
        eyebrow: "Autolavaggio",
        title: "Impianti di lavaggio Christ",
        desc: "Portali, tunnel, self-service e lavaggio mezzi pesanti. Concessionario ufficiale Christ Wash Systems per la Calabria.",
        img: "/christ/cadis.jpg",
        logo: "/brands/christ-logo.png",
        partner: "Christ Wash Systems",
        logoBg: "bg-white",
        accent: "bg-[#E30613]",
        cta: "Scopri gli impianti",
        testid: "hero-world-autolavaggio",
    },
    {
        id: "autoriparatore",
        num: "02",
        eyebrow: "Autoriparatore",
        title: "Attrezzature per l'officina",
        desc: "Ponti sollevatori, smontagomme, equilibratrici, assetto ruote e linee revisione. Partner Ravaglioli nel Sud Italia.",
        img: "/officina/officina-ravaglioli.jpg",
        logo: "/brands/ravaglioli-logo-dark.png",
        partner: "Ravaglioli",
        logoBg: "bg-white",
        accent: "bg-[#0B1F3A]",
        cta: "Scopri l'officina",
        testid: "hero-world-autoriparatore",
    },
];

const OTHERS = [
    { icon: Warehouse, label: "Scaffalature industriali" },
    { icon: Droplets, label: "Depurazione acque" },
    { icon: Umbrella, label: "Coperture esterne" },
    { icon: Factory, label: "Impianti industriali" },
];

const WorldCard = ({ w, delay }) => (
    <motion.article
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => scrollToSection(w.id)}
        data-testid={w.testid}
        className="group relative flex h-[380px] cursor-pointer flex-col overflow-hidden rounded-[1.75rem] bg-[#0B1F3A] shadow-[0_24px_60px_-20px_rgba(11,31,58,0.35)] sm:h-[420px] lg:h-[680px]"
    >
        <div className="relative h-[52%] overflow-hidden lg:h-[56%]">
            <img
                src={w.img}
                alt={w.title}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A] via-transparent to-[#0B1F3A]/30" />
            <span className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-white/95 py-1.5 pl-2 pr-3.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0B1F3A] shadow-lg backdrop-blur">
                <span className={`flex h-5 w-5 items-center justify-center rounded-full ${w.accent} text-[9px] text-white`}>
                    {w.num}
                </span>
                {w.eyebrow}
            </span>
        </div>
        <div className="relative flex flex-1 flex-col justify-between p-6 text-white sm:p-7">
            <div>
                <div className={`inline-flex items-center rounded-lg ${w.logoBg} px-3 py-1.5 ring-1 ring-white/10`}>
                    <img src={w.logo} alt={w.partner} className="h-6 w-auto" />
                </div>
                <h2 className="font-display mt-4 text-2xl font-extrabold tracking-tight sm:text-[1.7rem] sm:leading-tight">
                    {w.title}
                </h2>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-white/70">{w.desc}</p>
            </div>
            <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold">
                {w.cta}
                <ArrowRight className="h-4 w-4 text-[#E30613] transition-transform duration-300 group-hover:translate-x-1" />
            </span>
        </div>
        <div className={`absolute bottom-0 left-0 h-1 w-full ${w.accent} origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100`} />
    </motion.article>
);

export const Hero = () => (
    <section data-testid="hero-section" className="relative overflow-hidden bg-white pt-[76px]">
        <div aria-hidden="true" className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-[#E30613]/[0.05] blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-[#0B1F3A]/[0.05] blur-3xl" />

        <div className="mx-auto max-w-[1440px] px-4 pb-16 pt-12 sm:px-8 lg:px-16 lg:pb-24 lg:pt-20">
            <div className="grid gap-12 lg:grid-cols-12 lg:gap-10">
                <div className="lg:col-span-5">
                    <motion.span
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#E30613]"
                        data-testid="hero-eyebrow"
                    >
                        FTM Group S.r.l. · Castrolibero (CS) · Sud Italia
                    </motion.span>
                    <h1 className="font-display mt-6 text-4xl font-extrabold leading-[1.06] tracking-tight text-[#0B1F3A] sm:text-5xl lg:text-[3.9rem]">
                        <MaskedLine delay={0.15}>Impianti e attrezzature</MaskedLine>
                        <MaskedLine delay={0.28}>
                            che fanno <span className="text-[#E30613]">crescere</span>
                        </MaskedLine>
                        <MaskedLine delay={0.41}>la tua attività.</MaskedLine>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-7 max-w-md text-base leading-relaxed text-[#475569] sm:text-lg"
                        data-testid="hero-subtitle"
                    >
                        Due settori distinti, un solo partner tecnico: gli impianti di autolavaggio
                        e le attrezzature per l'autoriparatore. Forniamo, installiamo e assistiamo
                        concessionarie, stazioni di servizio, officine, gommisti, carrozzerie e
                        aziende di trasporto.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-9 flex flex-col gap-4 sm:flex-row"
                    >
                        <button
                            data-testid="hero-cta-primary"
                            onClick={() => scrollToSection("contatti")}
                            className="group flex items-center justify-center gap-2.5 rounded-full bg-[#E30613] px-8 py-4 text-base font-bold text-white transition-[background-color,box-shadow,transform] duration-300 hover:bg-[#C20410] hover:shadow-[0_16px_40px_-10px_rgba(227,6,19,0.55)] active:scale-95"
                        >
                            Richiedi Consulenza Gratuita
                            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <button
                            data-testid="hero-cta-secondary"
                            onClick={() => scrollToSection("servizi")}
                            className="flex items-center justify-center gap-2.5 rounded-full border-2 border-[#0B1F3A] px-8 py-4 text-base font-bold text-[#0B1F3A] transition-[background-color,color] duration-300 hover:bg-[#0B1F3A] hover:text-white"
                        >
                            Tutte le Divisioni
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.95 }}
                        className="mt-12"
                        data-testid="hero-other-divisions"
                    >
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#94A3B8]">
                            E inoltre
                        </div>
                        <ul className="mt-3 grid grid-cols-2 gap-2">
                            {OTHERS.map((o) => (
                                <li key={o.label}>
                                    <button
                                        type="button"
                                        onClick={() => scrollToSection("servizi")}
                                        className="flex w-full items-center gap-2.5 rounded-xl border border-slate-200 px-3 py-2.5 text-left text-xs font-semibold text-[#0B1F3A] transition-colors hover:border-[#0B1F3A]/40"
                                    >
                                        <o.icon className="h-4 w-4 shrink-0 text-[#E30613]" strokeWidth={1.8} />
                                        {o.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2 lg:col-span-7" data-testid="hero-worlds">
                    {WORLDS.map((w, i) => (
                        <WorldCard key={w.id} w={w} delay={0.35 + i * 0.15} />
                    ))}
                </div>
            </div>
        </div>

        <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            onClick={() => scrollToSection("christ")}
            className="absolute bottom-4 left-1/2 hidden -translate-x-1/2 text-[#0B1F3A]/40 transition-colors hover:text-[#E30613] lg:block"
            aria-label="Scorri in basso"
            data-testid="hero-scroll-indicator"
        >
            <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} className="block">
                <ChevronDown className="h-6 w-6" />
            </motion.span>
        </motion.button>
    </section>
);
